import React from 'react';
import { UniversalLink } from '@plone/volto/components';
import type { HeadingLevel } from 'volto-ministerio-publico-federal/types/heading';

export interface SidebarLinkCardItem {
  key: string;
  href: string;
  label: string;
  /** Força nova aba mesmo para link interno. Externo já abre em nova aba
   *  por padrão via `UniversalLink`, a menos que isto seja `false`. */
  openInNewTab?: boolean;
}

interface SidebarLinkCardProps {
  title: string;
  items: SidebarLinkCardItem[];
  emptyMessage: string;
  isEditMode?: boolean;
  /**
   * Nível do heading do card. O default `h2` vale quando o card é um bloco de
   * primeiro nível da página; dentro de uma seção que já tem h2, passe `h3`.
   */
  as?: HeadingLevel;
}

/**
 * Card genérico de sidebar para uma lista de links — mesmo molde visual do
 * Menu de Contexto (`.mpf-side-menu__content`): fundo branco, borda
 * `outline-var`, cantos arredondados, cabeçalho com título e divisor, cada
 * item como pill (`primary-container`/`on-primary-container`).
 *
 * Compartilhado pelos blocos "Serviços em Destaque" (RF05) e "Conteúdos
 * Relacionados" (RF06) para os dois ficarem visualmente idênticos sem
 * duplicar CSS — ver `_sidebar-link-card.scss`.
 */
export const SidebarLinkCard: React.FC<SidebarLinkCardProps> = ({
  title,
  items,
  emptyMessage,
  isEditMode,
  as: Heading = 'h2',
}) => {
  if (!items.length) {
    // Sem itens: some no site público, mas continua visível no editor de
    // blocos para o editor conseguir adicionar o primeiro item.
    if (!isEditMode) {
      return null;
    }
    return (
      <section className="mpf-sidebar-link-card">
        <header className="mpf-sidebar-link-card__header">
          <Heading className="mpf-sidebar-link-card__title">{title}</Heading>
        </header>
        <p className="mpf-sidebar-link-card__empty">{emptyMessage}</p>
      </section>
    );
  }

  return (
    <section className="mpf-sidebar-link-card">
      <header className="mpf-sidebar-link-card__header">
        <Heading className="mpf-sidebar-link-card__title">{title}</Heading>
      </header>

      <div className="mpf-sidebar-link-card__list">
        {items.map((item) => (
          <UniversalLink
            key={item.key}
            href={item.href}
            openLinkInNewTab={item.openInNewTab}
            className="mpf-sidebar-link-card__item"
          >
            <span className="mpf-sidebar-link-card__label">{item.label}</span>
          </UniversalLink>
        ))}
      </div>
    </section>
  );
};

export default SidebarLinkCard;
